// src/components/home/add-brand/AddBrand.tsx
import React, { useState, useCallback } from "react";
import { useAddBrand } from "../../../hooks/useAddBrand";
import { AddBrandProps } from "../../../types/add-brand-types";
import Form from "../../uiComponents/Form";
import FormField from "../../uiComponents/FormField";
import Layout from "../../layout/Layout";
import PromptModal from "../../uiComponents/PromptModal";
import "./addbrand.css";

const AddBrand: React.FC<AddBrandProps> = ({
  onSuccess,
  onCancel,
  embedded = false,
}) => {
  const { initialValues, validationRules, handleSubmit, handleCancel } =
    useAddBrand({ onSuccess, onCancel });

  const [toneOptions, setToneOptions] = useState([
    { value: "professional", label: "Professional" },
    { value: "witty", label: "Witty" },
    { value: "urgent", label: "Urgent" },
    { value: "friendly", label: "Friendly" },
  ]);
  const [showCustomToneModal, setShowCustomToneModal] = useState(false);

  const handleAddCustomTone = useCallback(() => {
    setShowCustomToneModal(true);
  }, []);

  const handleCustomToneSubmit = useCallback((customTone: string) => {
    const value = customTone.toLowerCase().replace(/\s+/g, "_");
    const label = customTone.trim();
    setToneOptions((prev) => {
      if (prev.some((o) => o.value === value)) return prev;
      return [...prev, { value, label }];
    });
  }, []);

  const formContent = (
    <div className={embedded ? "add-brand-content add-brand-embedded" : "add-brand-content"}>
          <Form
            initialValues={initialValues}
            validationRules={validationRules}
            onSubmit={handleSubmit}
          >
            {(formContext: any) => (
              <>
                {/* Brand Name */}
                <div className="form-section">
                  <FormField
                    name="brand_name"
                    label="Brand Name"
                    type="text"
                    placeholder="e.g. Acme Innovations"
                    formContext={formContext}
                    required
                    style={{ width: "100%" }}
                  />
                </div>

                {/* Brand URL */}
                <div className="form-section">
                  <FormField
                    name="brand_url"
                    label="Brand URL"
                    type="url"
                    placeholder="e.g. https://acme.com"
                    formContext={formContext}
                    required
                    style={{ width: "100%" }}
                  />
                </div>

                {/* Brand Tone and Target Audience */}
                <div className="form-col">
                  <FormField
                    name="brand_tone"
                    label="Brand Tone"
                    as="chip"
                    options={toneOptions}
                    onAddCustom={handleAddCustomTone}
                    formContext={formContext}
                    required
                  />
                </div>
                <div className="form-col">
                  <FormField
                    name="target_audience"
                    label="Target Audience"
                    type="text"
                    placeholder="e.g. CTOs, Marketers"
                    formContext={formContext}
                    required={false}
                    style={{ width: "100%" }}
                  />
                </div>
                {/* Key Competitors */}
                <div className="form-section">
                  <FormField
                    name="competitors"
                    label="Key Competitors"
                    as="tags"
                    placeholder="Add a competitor..."
                    helperText="Competitors you want to monitor or compare against."
                    formContext={formContext}
                  />
                </div>

                {/* Banned Words */}
                <div className="form-section">
                  <FormField
                    name="banned_words"
                    label="Banned Words"
                    as="tags"
                    placeholder="Add a word..."
                    helperText="Words the AI should never use in drafts."
                    formContext={formContext}
                  />
                </div>

                {/* Connected Platforms */}
                {/* <div className="form-section">
                  <label className="form-label">Connected Platforms</label>
                  <div className="platform-checkboxes">
                    <label className="platform-checkbox">
                      <input
                        type="checkbox"
                        checked={connectedPlatforms.linkedin}
                        onChange={() => handlePlatformToggle("linkedIn")}
                      />
                      <span>LinkedIn</span>
                    </label>
                    <label className="platform-checkbox">
                      <input
                        type="checkbox"
                        checked={connectedPlatforms.twitter}
                        onChange={() => handlePlatformToggle("twitter")}
                      />
                      <span>Twitter/X</span>
                    </label>
                    <label className="platform-checkbox">
                      <input
                        type="checkbox"
                        checked={connectedPlatforms.blog}
                        onChange={() => handlePlatformToggle("blog")}
                      />
                      <span>Blog</span>
                    </label>
                  </div>
                </div> */}

                {/* Footer Buttons */}
                <div className="form-footer">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={formContext.handleSubmit}
                    disabled={formContext.isSubmitting}
                  >
                    {formContext.isSubmitting ? "Creating..." : "Create Brand"}
                  </button>
                </div>
              </>
            )}
          </Form>
    </div>
  );

  if (embedded) {
    return (
      <>
        {formContent}
        <PromptModal
          isOpen={showCustomToneModal}
          onClose={() => setShowCustomToneModal(false)}
          title="Custom tone"
          message="Enter a custom brand tone:"
          placeholder="e.g. Bold, Casual"
          onSubmit={handleCustomToneSubmit}
          submitLabel="Add"
        />
      </>
    );
  }

  return (
    <>
      <Layout>
        <div className="add-brand-container">
          <div className="add-brand-header">
            <div>
              <h1 className="add-brand-title">Define Your Brand Strategy</h1>
              <p className="add-brand-subtitle">
                Teach the AI agent how to speak for your company.
              </p>
            </div>
            <button className="back-button" onClick={handleCancel}>
              ← Back
            </button>
          </div>
          {formContent}
        </div>
      </Layout>
      <PromptModal
        isOpen={showCustomToneModal}
        onClose={() => setShowCustomToneModal(false)}
        title="Custom tone"
        message="Enter a custom brand tone:"
        placeholder="e.g. Bold, Casual"
        onSubmit={handleCustomToneSubmit}
        submitLabel="Add"
      />
    </>
  );
};

export default AddBrand;
